import Route from '@ember/routing/route';
import { type Registry as Services, service } from '@ember/service';
import { task } from 'ember-concurrency';
import type { BillingAdmin } from 'frontend-payments/models';
import type { Model as AuthenticatedModel } from 'frontend-payments/routes/authenticated';
import type { ModelFrom } from 'frontend-payments/utils/routes';

export type Model = ModelFrom<AuthenticatedBillingAdminsRoute>;

export default class AuthenticatedBillingAdminsRoute extends Route {
  @service declare accountSwitcher: Services['account-switcher'];
  @service declare alerting: Services['alerting'];
  @service declare errorTracking: Services['error-tracking'];
  @service declare store: Services['store'];

  queryBillingAdminsTask = task({}, async (accountId: string) => {
    try {
      const apiResponse = await this.store.query<BillingAdmin>(
        'billing-admin',
        {
          adapterOptions: {
            namespace: `api/v4/accounts/${accountId}`,
          },
        },
      );
      return [...apiResponse];
    } catch (error: unknown) {
      this.errorTracking.captureException(error);
      await this.alerting.showErrorAlert();
      throw new Error('Failed to fetch billing admins');
    }
  });

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  model() {
    const accountId = this.accountSwitcher.currentAccountId;

    if (!accountId) {
      throw new Error('Account ID is required to fetch billing admins');
    }

    // Get parent model to access account loading state
    const parentModel = this.modelFor('authenticated') as AuthenticatedModel;

    return {
      billingAdmins: this.queryBillingAdminsTask.perform(accountId),
      accountId,
      parentAccount: parentModel.account,
    };
  }
}
